import { IS_APPLE } from "@lexical/utils";

type KeyboardEventModifiers = Pick<
  KeyboardEvent,
  "key" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey"
>;

/**
 * A record of keyboard modifiers that must be enabled.
 * If the value is `'any'` then the modifier key's state is ignored.
 * If the value is `true` then the modifier key must be pressed.
 * If the value is `false` or the property is omitted then the modifier key must
 * not be pressed.
 */
type KeyboardEventModifierMask = Partial<
  Record<
    Exclude<keyof KeyboardEventModifiers, "key">,
    boolean | undefined | "any"
  >
>;

function matchModifier(
  event: KeyboardEventModifiers,
  mask: KeyboardEventModifierMask,
  prop: keyof KeyboardEventModifierMask,
): boolean {
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const expected = mask[prop] || false;
  return expected === "any" || expected === event[prop];
}

/**
 * Match a KeyboardEvent with its expected modifier state
 *
 * @param event A KeyboardEvent, or structurally similar object
 * @param mask An object specifying the expected state of the modifiers
 * @returns true if the event matches
 */
export function isModifierMatch(
  event: KeyboardEventModifiers,
  mask: KeyboardEventModifierMask,
): boolean {
  return (
    matchModifier(event, mask, "altKey") &&
    matchModifier(event, mask, "ctrlKey") &&
    matchModifier(event, mask, "shiftKey") &&
    matchModifier(event, mask, "metaKey")
  );
}

export const SHORTCUTS = Object.freeze({
  // (Ctrl|⌘) + (Alt|Option) + <key> shortcuts
  NORMAL: IS_APPLE ? "⌘+Opt+0" : "Ctrl+Alt+0",
  HEADING1: IS_APPLE ? "⌘+Opt+1" : "Ctrl+Alt+1",
  HEADING2: IS_APPLE ? "⌘+Opt+2" : "Ctrl+Alt+2",
  HEADING3: IS_APPLE ? "⌘+Opt+3" : "Ctrl+Alt+3",
  BULLET_LIST: IS_APPLE ? "⌘+Opt+4" : "Ctrl+Alt+4",
  NUMBERED_LIST: IS_APPLE ? "⌘+Opt+5" : "Ctrl+Alt+5",

  // (Ctrl|⌘) + Shift + <key> shortcuts
  STRIKETHROUGH: IS_APPLE ? "⌘+Shift+S" : "Ctrl+Shift+S",

  // (Ctrl|⌘) + <key> shortcuts
  INDENT: IS_APPLE ? "⌘+]" : "Ctrl+]",
  OUTDENT: IS_APPLE ? "⌘+[" : "Ctrl+[",
  REDO: IS_APPLE ? "⌘+Shift+Z" : "Ctrl+Y",
  UNDO: IS_APPLE ? "⌘+Z" : "Ctrl+Z",
  BOLD: IS_APPLE ? "⌘+B" : "Ctrl+B",
  ITALIC: IS_APPLE ? "⌘+I" : "Ctrl+I",
  UNDERLINE: IS_APPLE ? "⌘+U" : "Ctrl+U",
});

const CONTROL_OR_META = { ctrlKey: !IS_APPLE, metaKey: IS_APPLE };

export function isFormatParagraph(event: KeyboardEvent): boolean {
  const { code } = event;

  return (
    (code === "Numpad0" || code === "Digit0") &&
    isModifierMatch(event, { ...CONTROL_OR_META, altKey: true })
  );
}

export function isFormatHeading(event: KeyboardEvent): boolean {
  const { code } = event;
  const keyNumber = code[code.length - 1];

  return Boolean(
    keyNumber &&
    ["1", "2", "3"].includes(keyNumber) &&
    isModifierMatch(event, { ...CONTROL_OR_META, altKey: true }),
  );
}

export function isFormatBulletList(event: KeyboardEvent): boolean {
  const { code } = event;
  return (
    (code === "Numpad4" || code === "Digit4") &&
    isModifierMatch(event, { ...CONTROL_OR_META, altKey: true })
  );
}

export function isFormatNumberedList(event: KeyboardEvent): boolean {
  const { code } = event;
  return (
    (code === "Numpad5" || code === "Digit5") &&
    isModifierMatch(event, { ...CONTROL_OR_META, altKey: true })
  );
}

export function isStrikeThrough(event: KeyboardEvent): boolean {
  const { code } = event;
  return (
    code === "KeyS" &&
    isModifierMatch(event, { ...CONTROL_OR_META, shiftKey: true })
  );
}

export function isIndent(event: KeyboardEvent): boolean {
  const { code } = event;
  return code === "BracketRight" && isModifierMatch(event, CONTROL_OR_META);
}

export function isOutdent(event: KeyboardEvent): boolean {
  const { code } = event;
  return code === "BracketLeft" && isModifierMatch(event, CONTROL_OR_META);
}
